import { useCallback, useState, type FormEvent } from "react"
import { Link, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TurnstileWidget } from "../components/turnstile-widget"
import { validatePlayerName } from "@/shared/validation/name"
import { getOrCreateDeviceToken } from "../lib/device"
import { formatNameError } from "../lib/error-messages"
import { writeStoredSession } from "../lib/ws-client"

interface CreateRoomResponse {
  roomCode: string
  hostPlayerId: string
  sessionToken: string
  requiresTurnstile?: boolean
  sitekey?: string
  error?: string
}

export function CreatePage() {
  const navigate = useNavigate()
  const [name, setName] = useState("")
  const [nameError, setNameError] = useState<string | null>(null)
  const [nameTouched, setNameTouched] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [turnstileSitekey, setTurnstileSitekey] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState("")
  const [imposterCount, setImposterCount] = useState<1 | 2 | 3>(1)
  const needsVerification = Boolean(turnstileSitekey)
  const canSubmit = !isCreating && (!needsVerification || Boolean(turnstileToken))

  const handleTurnstileToken = useCallback((token: string) => {
    setTurnstileToken(token)
  }, [])

  const handleTurnstileError = useCallback(() => {
    toast.error("Verification could not load")
  }, [])

  async function createRoom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setNameTouched(true)
    const validation = validatePlayerName(name)

    if (!validation.ok) {
      setNameError(formatNameError(validation.reason))
      return
    }

    setIsCreating(true)
    setNameError(null)

    const response = await fetch("/api/rooms/create", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        name: validation.displayName,
        deviceToken: getOrCreateDeviceToken(),
        imposterCount,
        turnstileToken: turnstileToken || undefined,
      }),
    })
    const payload = (await response.json()) as CreateRoomResponse
    setIsCreating(false)

    if (payload.requiresTurnstile) {
      setTurnstileSitekey(payload.sitekey ?? "1x00000000000000000000AA")
      setTurnstileToken("")
      toast.info("Verification required before creating another room")
      return
    }

    if (response.status === 403 && payload.error === "TURNSTILE_FAILED") {
      setTurnstileToken("")
      toast.error("Verification failed")
      return
    }

    if (response.status === 503 && payload.error === "TURNSTILE_NOT_CONFIGURED") {
      toast.error("Room creation is temporarily unavailable")
      return
    }

    if (!response.ok || payload.error) {
      toast.error("Could not create room")
      return
    }

    writeStoredSession({
      roomCode: payload.roomCode,
      playerId: payload.hostPlayerId,
      sessionToken: payload.sessionToken,
    })
    navigate(`/r/${payload.roomCode}`)
  }

  return (
    <main
      id="main"
      className="mx-auto flex min-h-dvh w-full max-w-md flex-col bg-background text-foreground"
    >
      <header className="border-b-2 border-border px-5 py-5">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.35em] text-muted-foreground transition-colors hover:text-foreground"
        >
          ← Back
        </Link>
        <h1 className="mt-3 font-display text-3xl uppercase leading-[0.95] tracking-tight">
          Host a <span className="text-primary">room</span>
        </h1>
        <p className="prose-body mt-2 max-w-[34ch] text-sm text-muted-foreground">
          Pick your name and how many imposters. You'll get a 6-letter code to share.
        </p>
      </header>

      <section className="flex-1 px-5 py-6">
        <form onSubmit={createRoom} className="flex flex-col gap-5">
          <label className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            Your name
            <Input
              value={name}
              onChange={(event) => {
                setName(event.target.value)
                if (nameTouched) {
                  const validation = validatePlayerName(event.target.value)
                  setNameError(validation.ok ? null : formatNameError(validation.reason))
                }
              }}
              onBlur={() => {
                setNameTouched(true)
                const validation = validatePlayerName(name)
                setNameError(validation.ok ? null : formatNameError(validation.reason))
              }}
              aria-invalid={Boolean(nameError)}
              autoComplete="given-name"
              inputMode="text"
              name="player-name"
              placeholder="SAM"
              className="h-12 bg-card text-base font-medium tracking-wide text-foreground placeholder:text-muted-foreground/50"
            />
            {nameError ? (
              <span className="text-sm normal-case tracking-normal text-primary">
                {nameError}
              </span>
            ) : null}
          </label>

          {turnstileSitekey ? (
            <div className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              <span>Verification</span>
              <div className="border-2 border-border bg-card p-3">
                <TurnstileWidget
                  sitekey={turnstileSitekey}
                  onToken={handleTurnstileToken}
                  onError={handleTurnstileError}
                />
              </div>
            </div>
          ) : null}

          <fieldset className="flex flex-col gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
            <legend>Imposters per round</legend>
            <div role="radiogroup" aria-label="Number of imposters" className="flex gap-2">
              {([1, 2, 3] as const).map((count) => {
                const selected = imposterCount === count
                return (
                  <button
                    key={count}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setImposterCount(count)}
                    className={`flex h-14 flex-1 items-center justify-center border-2 font-display text-2xl transition-colors ${
                      selected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground hover:border-foreground"
                    }`}
                  >
                    {count}
                  </button>
                )
              })}
            </div>
            <p className="prose-body normal-case tracking-normal text-[11px] text-muted-foreground">
              {imposterCount === 1 && "Classic. Works with 3+ players."}
              {imposterCount === 2 && "Spicy. Needs 4+ players for fair play."}
              {imposterCount === 3 && "Chaos. Needs 6+ players. Team gets one shot."}
            </p>
          </fieldset>

          <Button
            type="submit"
            disabled={!canSubmit}
            className="mt-2 h-14 bg-primary text-primary-foreground hover:bg-primary"
          >
            {isCreating ? "Opening room..." : "Create room"}
          </Button>
        </form>
      </section>
    </main>
  )
}
